import { io } from 'socket.io-client';

// Track processed sync IDs
const processedSyncIds = new Set();
let socket;
let listenersInitialized = false;

function setupLogging() {
  if (!socket || listenersInitialized) return;

  // Connection lifecycle
  socket.on('connect', () => {
    const transport = socket?.io?.engine?.transport?.name;
    console.log(`Socket connected! ID: ${socket.id}, Transport: ${transport}`);
  });
  socket.on('disconnect', (reason) => {
    console.warn('Socket disconnected:', reason);
  });
  socket.on('connect_error', (err) => {
    console.error('Socket connect_error:', err?.message || err);
  });

  // Manager-level reconnect events
  const manager = socket.io;
  if (manager && manager.on) {
    manager.on('reconnect_attempt', (attempt) => {
      console.log('Socket reconnect_attempt:', attempt);
    });
    manager.on('reconnect', (attempt) => {
      console.log('Socket reconnected on attempt:', attempt);
    });
    manager.on('reconnect_error', (err) => {
      console.warn('Socket reconnect_error:', err?.message || err);
    });
    manager.on('reconnect_failed', () => {
      console.error('Socket reconnect_failed');
    });
  }

  // Engine/WebSocket upgrade diagnostics
  const engine = socket?.io?.engine;
  if (engine && engine.on) {
    engine.on('upgrade', (transport) => {
      console.log('Engine transport upgraded to:', transport?.name);
    });
    engine.on('close', (reason) => {
      console.log('Engine closed:', reason);
    });
    engine.on('error', (err) => {
      console.warn('Engine error:', err);
    });
  }

  listenersInitialized = true;
}

export const socketService = {
  connect: () => {
    // Use environment variable for Socket.IO server URL
    const serverURL = process.env.REACT_APP_API_BASE_URL.replace(/\/api$/, '/');

    if (!socket) {
      console.log(`Connecting to Socket.IO server at: ${serverURL}`);
      // Prefer default behavior: start with polling then upgrade to websocket
      socket = io(serverURL, {
        path: '/socket.io',
        // keep polling available (server supports it) and allow upgrade to websocket
        transports: ['polling', 'websocket'],
        upgrade: true,
        rememberUpgrade: true,
        reconnection: true, // infinite by default
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 15000
      });
      listenersInitialized = false;
      setupLogging();
      return socket;
    }

    if (socket.connected) {
      console.log('Socket already connected');
      return socket;
    }

    // Reconnect existing socket instance to preserve listeners
    try {
      console.log('Attempting to connect existing socket instance...');
      socket.connect();
    } catch (e) {
      console.warn('Error connecting existing socket, recreating...', e);
      socket = undefined;
      return socketService.connect();
    }
    return socket;
  },

  // Force a hard reconnection while preserving event handlers
  forceReconnect: () => {
    if (!socket) return socketService.connect();
    try {
      console.log('Force reconnect: closing manager and reopening...');
      const manager = socket.io;
      // Fully close underlying engine/manager, then reopen
      if (manager?.close) manager.close();
      if (manager?.open) manager.open(); else socket.connect();
    } catch (e) {
      console.warn('Force reconnect fallback path due to error:', e);
      try { socket.disconnect(); } catch {}
      setTimeout(() => {
        try { socket.connect(); } catch { /* no-op */ }
      }, 100);
    }
  },
  
  disconnect: () => {
    if (socket) {
      try {
        socket.disconnect();
      } catch {}
    }
  },
  
  // Check connection status
  isConnected: () => !!(socket && socket.connected),
  
  // Subscribe to events
  onConnect: (callback) => {
    if (!socket) return () => {};
    socket.on('connect', callback);
    return () => socket.off('connect', callback);
  },
  
  onDisconnect: (callback) => {
    if (!socket) return () => {};
    socket.on('disconnect', callback);
    return () => socket.off('disconnect', callback);
  },
  
  onBookingCreated: (callback) => {
    if (!socket) return () => {};
    socket.on('booking-created', callback);
    return () => socket.off('booking-created', callback);
  },
  
  onBookingUpdated: (callback) => {
    if (!socket) return () => {};
    socket.on('booking-updated', callback);
    return () => socket.off('booking-updated', callback);
  },
  
  onBookingDeleted: (callback) => {
    if (!socket) return () => {};
    socket.on('booking-deleted', callback);
    return () => socket.off('booking-deleted', callback);
  },
  
  // Get the socket instance for advanced usage
  getSocket: () => socket,
  
  // Online users functionality
  joinAsUser: (userInfo) => {
    if (socket && userInfo) {
      console.log('Joining as user:', userInfo.name);
      socket.emit('user-join', userInfo);
    }
  },
  
  leaveAsUser: () => {
    if (socket) {
      console.log('Leaving as user');
      socket.emit('user-leave');
    }
  },
  
  onOnlineUsersUpdated: (callback) => {
    if (!socket) return () => {};
    socket.on('online-users-updated', callback);
    return () => socket.off('online-users-updated', callback);
  },
  
  onUserProfileUpdated: (callback) => {
    if (!socket) return () => {};
    socket.on('user-profile-updated', callback);
    return () => socket.off('user-profile-updated', callback);
  },
  
  // Add tracking of processed events
  hasSyncIdBeenProcessed: (syncId) => {
    return processedSyncIds.has(syncId);
  },
  
  isEquipmentSyncEvent: (syncId) => {
    if (!syncId) return false;
    return syncId.includes('-wing') || 
           syncId.includes('-foil') || 
           syncId.includes('-board');
  },
  
  // Complete markSyncIdAsProcessed implementation for equipment events
  markSyncIdAsProcessed: (syncId) => {
    if (!syncId) return;
    
    // For equipment sync events, store both the raw ID and the base ID
    processedSyncIds.add(syncId);
    
    // If it's an equipment-specific event, also track the base ID
    if (socketService.isEquipmentSyncEvent(syncId)) {
      // Extract the base sync ID (before the equipment suffix)
      const baseSyncId = syncId.split('-wing')[0].split('-foil')[0].split('-board')[0];
      if (baseSyncId) {
        processedSyncIds.add(baseSyncId);
        console.log(`Marked base event ${baseSyncId} as processed`);
      }
    }
    
    // Clean up old sync IDs to prevent memory leaks
    while (processedSyncIds.size > 1000) {
      const iterator = processedSyncIds.values();
      processedSyncIds.delete(iterator.next().value);
    }
  }
};